/**
 * Allys Area Page Component
 * 
 * Complete area page layout combining all area sub-components.
 * Used for SEO-focused neighbourhood/region pages.
 */

'use client';

import { useEffect, useState } from 'react';
import type { AreaPageProps, AreaWeatherData } from '@routeroyal/theme-core';
import { fetchAreaWeather } from '@routeroyal/theme-core';

import { AllysAreaHero } from './AllysAreaHero';
import { AllysAreaOverview } from './AllysAreaOverview';
import { AllysAreaStats } from './AllysAreaStats';
import { AllysAreaMap } from './AllysAreaMap';
import { AllysAreaProperties } from './AllysAreaProperties';
import { AllysAreaSelector } from './AllysAreaSelector';

export interface AllysAreaPageProps extends AreaPageProps {
  /**
   * Show weather data (fetched client-side)
   * @default true
   */
  showWeather?: boolean;
  
  /**
   * Show property listings
   * @default true
   */
  showProperties?: boolean;
  
  /**
   * Show area selector
   * @default true
   */
  showAreaSelector?: boolean;
}

export function AllysAreaPage({
  data,
  regions,
  locale = 'en',
  messages,
  propertyBasePath = '/properties',
  areaBasePath = '/areas',
  showWeather = true,
  showProperties = true,
  showAreaSelector = true,
  className = '',
}: AllysAreaPageProps) {
  const [weather, setWeather] = useState<AreaWeatherData | null>(data.weather || null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  // Fetch weather data client-side
  useEffect(() => {
    if (!showWeather || data.weather) return;

    const loadWeather = async () => {
      setWeatherLoading(true);
      try {
        const weatherData = await fetchAreaWeather(data.latitude, data.longitude);
        if (weatherData) {
          setWeather(weatherData);
        }
      } catch (error) {
        console.error('Failed to fetch weather:', error);
      } finally {
        setWeatherLoading(false);
      }
    };

    loadWeather();
  }, [data.latitude, data.longitude, data.weather, showWeather]);

  // Generate view all properties link
  const viewAllLink = `/${locale}${propertyBasePath}?area=${data.slug}`;

  return (
    <div 
      className={`font-sans text-[var(--color-text)] bg-[#f8f9fa] min-h-screen selection:bg-[var(--color-primary)] selection:text-white overflow-x-hidden ${className}`}
    >
      {/* Hero Section */}
      <AllysAreaHero area={data} />

      {/* Main Content Container */}
      <div className="relative z-30 container mx-auto px-6 md:px-12 -mt-24 md:-mt-48 pb-20">
        
        {/* Floating Title Card */}
        <div className="bg-white rounded-[2rem] p-8 md:p-12 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15)] mb-12 flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 relative overflow-hidden transition-all duration-500 border border-gray-100/80">
          {/* Decorative Background Element */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--color-primary)]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

          <div className="relative z-10 w-full lg:w-2/3">
            <div className="flex items-center gap-4 mb-4">
              <span 
                className="px-4 py-1.5 rounded-full border border-[var(--color-primary)]/30 text-[var(--color-primary)] text-[10px] font-bold tracking-[0.2em] uppercase bg-[var(--color-primary)]/5 font-inter"
              >
                {data.region}
              </span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl text-[var(--color-text)] leading-[1.1]">
              {data.name}
            </h1>
          </div>

          <div className="relative z-10 w-full lg:w-1/3 flex flex-col items-start lg:items-end">
            <div className="p-6 bg-gray-50/80 rounded-2xl border border-gray-100 w-full lg:w-auto backdrop-blur-sm">
              <div className="flex items-center gap-3 text-[var(--color-text-muted)] mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" className="opacity-60"><path fill="currentColor" d="M128 24a104 104 0 1 0 104 104A104.12 104.12 0 0 0 128 24Zm88 104a87.6 87.6 0 0 1-3.33 24H174.2a159.5 159.5 0 0 0 0-48h38.47A87.6 87.6 0 0 1 216 128Zm-88 88a87.5 87.5 0 0 1-30.86-56h61.72A87.5 87.5 0 0 1 128 216ZM97.14 96a87.5 87.5 0 0 1 61.72 0 87.5 87.5 0 0 1 30.86 56H97.14a87.5 87.5 0 0 1 30.86-56ZM40 128a87.6 87.6 0 0 1 3.33-24h38.47a159.5 159.5 0 0 0 0 48H43.33A87.6 87.6 0 0 1 40 128Z"/></svg>
                <span 
                  className="text-xs font-mono uppercase tracking-widest font-inter"
                >
                  {messages?.coordinates || 'Coordinates'}
                </span>
              </div>
              <p className="font-serif text-xl md:text-2xl text-[var(--color-text)] italic">
                {data.coordinates.split(',')[0]?.trim()}
              </p>
              <p className="font-serif text-xl md:text-2xl text-[var(--color-primary)] italic">
                {data.coordinates.split(',')[1]?.trim()}
              </p>
            </div>
          </div>
        </div>

        {/* Layout Grid: Overview + Map Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          {/* About Section - Large Card */}
          <div className="lg:col-span-8 flex flex-col gap-8">
          {messages ? (
            <AllysAreaOverview 
              area={data}
              additionalContent={`This area represents the pinnacle of Mauritian living, offering residents an unparalleled lifestyle blended with authentic island culture. Whether you are seeking a permanent residence or a holiday retreat, ${data.name} provides privacy, security, and access to world-class amenities.`}
              messages={messages}
            />
          ) : (
            <AllysAreaOverview 
              area={data}
              additionalContent={`This area represents the pinnacle of Mauritian living, offering residents an unparalleled lifestyle blended with authentic island culture. Whether you are seeking a permanent residence or a holiday retreat, ${data.name} provides privacy, security, and access to world-class amenities.`}
            />
          )}
        </div>

          {/* Right Column: Map */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="h-full min-h-[500px] w-full">
            {messages ? (
              <AllysAreaMap 
                area={data}
                messages={messages}
              />
            ) : (
              <AllysAreaMap area={data} />
            )}
            </div>
          </div>
          </div>
          
        {/* Stats Section - Full Width */}
        <div className="mb-12">
            <AllysAreaStats 
              area={data}
              weatherLoading={weatherLoading}
              {...(weather ? { weather } : {})}
              {...(data.marketStats ? { marketStats: data.marketStats } : {})}
              {...(messages ? { messages } : {})}
            />
        </div>
      </div>

      {/* Properties Section */}
      {showProperties && data.properties && data.properties.length > 0 && (
        <AllysAreaProperties
          properties={data.properties}
          areaName={data.name}
          viewAllLink={viewAllLink}
          maxItems={3}
          propertyBasePath={propertyBasePath}
          {...(messages ? { messages } : {})}
          locale={locale}
        />
      )}

      {/* Area Selector */}
      {showAreaSelector && (
        <AllysAreaSelector
          regions={regions}
          currentArea={data.slug}
          basePath={areaBasePath}
          {...(messages ? { messages } : {})}
          locale={locale}
        />
      )}
    </div>
  );
}
